import { useForm } from 'react-hook-form';
import { handleServerFormError, trackError, ValidationError } from '#api/errors.ts';
import { useEditWebAuthnAuthenticator } from '#api/webauthn-authenticators.queries.ts';
import { Button } from '#components/atoms/buttons/Button.tsx';
import { ErrorMessage, Input } from '#components/atoms/forms/index.ts';
import { ConfirmButtonGroup } from '#components/patterns/confirmations/confirm-button-group/ConfirmButtonGroup.tsx';
import { Field } from '#components/patterns/forms/field/Field.tsx';
import { ModalBody, ModalFooter } from '#components/patterns/modal/index.ts';
import { Spacer } from '#components/patterns/spacer/Spacer.tsx';

const FORM_NAME = 'edit-webauthn-form';

type FormData = {
  name: string;
};

type Props = {
  onSuccess: () => void;
  onCancel: () => void;
  defaultValues: FormData;
  webauthnID: string;
};

export function EditWebAuthnForm({ onSuccess, onCancel, webauthnID, defaultValues }: Props) {
  const { register, handleSubmit, formState, setError } = useForm<FormData>({
    defaultValues,
  });
  const editWebAuthn = useEditWebAuthnAuthenticator();
  const { errors, isSubmitting } = formState;

  const onSubmit = handleSubmit(async (data) => {
    try {
      await editWebAuthn.mutateAsync({
        id: webauthnID,
        values: {
          name: data.name,
        },
      });
      onSuccess();
    } catch (error) {
      if (error instanceof ValidationError) {
        handleServerFormError(error, ({ path, message }) => {
          switch (path) {
            case 'name':
              setError(path, { message }, { shouldFocus: true });
              return true;
            case undefined:
              setError('root.global', { message });
              return true;
            default:
              setError('root.global', { message });
              return false;
          }
        });
      } else {
        trackError(error);
        setError('root.global', { message: 'We couldn’t edit the passkey, please try again' });
      }
    }
  });

  return (
    <>
      <ModalBody>
        <form method="post" id={FORM_NAME} onSubmit={onSubmit}>
          <Spacer direction="vertical" size="wide" className="mb-16">
            {errors.root?.global?.message && <ErrorMessage>{errors.root.global.message}</ErrorMessage>}
            <Field label="Name">
              <Input
                className="width-100"
                hasError={Boolean(errors.name)}
                {...register('name', {
                  maxLength: { value: 50, message: 'Passkey name cannot be longer than 50 characters' },
                })}
              />
              {errors.name && <ErrorMessage>{errors.name.message}</ErrorMessage>}
            </Field>
          </Spacer>
        </form>
      </ModalBody>
      <ModalFooter>
        <ConfirmButtonGroup
          variant="right"
          primaryButton={
            <Button type="submit" form={FORM_NAME} disabled={isSubmitting}>
              Save
            </Button>
          }
          secondaryButton={
            <Button variant="link" onClick={onCancel}>
              Cancel
            </Button>
          }
        />
      </ModalFooter>
    </>
  );
}
